import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDesign } from '../contexts/DesignContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, Loader2, KeyRound, CheckCircle, Dumbbell, Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown } from 'lucide-react';
import { authApi } from '../api';

const LOGO_ICONS = { Dumbbell, Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown };

export default function ForgotPasswordPage() {
  const { design } = useDesign();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState('email'); // email | code | done
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const pc = design.primary_color || '#FF5C00';
  const LogoIcon = LOGO_ICONS[design.logo_icon] || Dumbbell;
  const appLeft = design.app_name_left || 'trainedby';
  const appRight = design.app_name_right || 'RS';
  const hf = design.heading_font || 'Barlow Condensed';

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      toast.success(t('forgotPassword.codeSent'));
      setStep('code');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('forgotPassword.sendCodeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t('forgotPassword.passwordsMustMatch'));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('forgotPassword.passwordTooShort'));
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(email, code, newPassword);
      toast.success(t('forgotPassword.resetSuccess'));
      setStep('done');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('forgotPassword.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(135deg, ${pc}08 0%, transparent 50%)` }}>
      <Card className="w-full max-w-md border-border/50" data-testid="forgot-password-page">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-2.5 rounded-xl" style={{ backgroundColor: pc, boxShadow: `0 0 20px ${pc}26` }}>
              <LogoIcon className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold" style={{ fontFamily: `${hf}, sans-serif` }}>
              {appLeft}<span style={{ color: pc }}>{appRight}</span>
            </span>
          </div>
          <CardTitle className="text-xl" style={{ fontFamily: `${hf}, sans-serif` }}>
            {step === 'email' && t('forgotPassword.titleEmail')}
            {step === 'code' && t('forgotPassword.titleCode')}
            {step === 'done' && t('forgotPassword.titleDone')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {step === 'email' && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                {t('forgotPassword.emailDesc')}
              </p>
              <div className="space-y-2">
                <Label htmlFor="email">{t('forgotPassword.emailLabel')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full text-white" style={{ backgroundColor: pc }} disabled={loading} data-testid="send-reset-code-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                {t('forgotPassword.sendCode')}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm hover:underline" style={{ color: pc }}>
                  <ArrowLeft className="inline h-3 w-3 mr-1" />{t('forgotPassword.backToLogin')}
                </Link>
              </div>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                {t('forgotPassword.codeDesc', { email })}
              </p>
              <div className="space-y-2">
                <Label htmlFor="code">{t('forgotPassword.resetCodeLabel')}</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="pl-10 text-center text-lg tracking-widest"
                    maxLength={6}
                    required
                    data-testid="reset-code-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">{t('forgotPassword.newPasswordLabel')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder={t('forgotPassword.newPasswordPlaceholder')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('forgotPassword.confirmPasswordLabel')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder={t('forgotPassword.confirmPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full text-white" style={{ backgroundColor: pc }} disabled={loading} data-testid="reset-password-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                {t('forgotPassword.resetPassword')}
              </Button>
              <div className="flex justify-between text-sm">
                <button type="button" onClick={() => setStep('email')} className="hover:underline" style={{ color: pc }}>
                  {t('forgotPassword.changeEmail')}
                </button>
                <button type="button" onClick={handleSendCode} className="hover:underline text-muted-foreground">
                  {t('forgotPassword.resendCode')}
                </button>
              </div>
            </form>
          )}

          {step === 'done' && (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${pc}20` }}>
                <CheckCircle className="h-8 w-8" style={{ color: pc }} />
              </div>
              <p className="text-muted-foreground">{t('forgotPassword.successMessage')}</p>
              <Button onClick={() => navigate('/login')} className="w-full text-white" style={{ backgroundColor: pc }} data-testid="go-to-login-btn">
                {t('forgotPassword.goToLogin')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
